import {
	debounce,
	pauseAllMedia,
	PUB_SUB_EVENTS,
	subscribe,
	VariantChangeImmediateEvent,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {
	isSliderComponent,
	type SliderComponent,
} from '@/scripts/theme/slider-component'
import { type StickyHeader } from '@/scripts/theme/sticky-header'
import { type DeferredMedia } from '@/scripts/theme/deferred-media'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class MediaGallery extends UcoastEl {
	static htmlSelector = 'media-gallery'
	elements: {
		liveRegion: HTMLElement
		viewer: SliderComponent
	}
	mql: MediaQueryList
	stickyHeader?: StickyHeader
	productId: string
	customVariantMedia?: Record<string, string[]>
	constructor() {
		super()
		this.elements = {
			liveRegion: q.rs('[id^="GalleryStatus"]', this),
			viewer: q.rs('[id^="GalleryViewer"]', this),
		}
		this.productId = q.ra(this, 'data-product-id')
		this.mql = window.matchMedia('(min-width: 750px)')
		this.customVariantMedia = this.getCustomVariantMedia()

		subscribe(
			PUB_SUB_EVENTS.variantChangeImmediate,
			this.switchToFeaturedMedia.bind(this)
		)
	}

	getCustomVariantMedia() {
		const json = q.os<HTMLScriptElement>(
			'script[type="application/json"][data-variant-media-ids]',
			this
		)?.textContent
		if (!json) return
		try {
			return JSON.parse(json) as Record<string, string[]>
		} catch (error) {
			console.error(
				'Error parsing JSON, check variant-media-ids-from-metafield.liquid and make sure theres no commas that shouldnt be there',
				error
			)
		}
	}

	switchToFeaturedMedia(event: VariantChangeImmediateEvent) {
		const variantId = event.data.variantId
		 const usedCustomGallery = this.switchCustomMedia(variantId)
    if (usedCustomGallery) return
		const featuredMedia = q.rs(
			`[data-uc-featured-media][data-variant-id="${variantId}"]`,
			this.elements.viewer
		)
		const featuredMediaId = q.ra(featuredMedia, 'data-gallery-media-id')
		this.setActiveMedia(featuredMediaId, false)
	}

	getActiveMediaParent(mediaId: string) {
		return q.rs(
			`[data-gallery-media-id="${mediaId}"]`,
			this.elements.viewer,
			'parentElement'
		)
	}

	setActiveMedia(mediaId: string, prepend: boolean) {
		const activeMedia = q.rs(
			`[data-gallery-media-id="${mediaId}"]`,
			this.elements.viewer
		)
		q.rl('[data-gallery-media-id]', this.elements.viewer).forEach((element) => {
			element.classList.remove('is-active')
      const elMediaId = q.ra(element, 'data-gallery-media-id')
      if (elMediaId=== mediaId) {
        element.classList.remove('hidden')
        element.classList.add('is-active')
        void window.Ucoast.mediaManager.playAllInContainer(element)
      } else {
        element.classList.add('hidden')
        element.classList.remove('is-active')
        void window.Ucoast.mediaManager.pauseAllInContainer(element)
      }
		})

		if (prepend) {
			const activeMediaParent = this.getActiveMediaParent(mediaId)
			activeMediaParent.prepend(activeMedia)

			if (isSliderComponent(this.elements.viewer))
				this.elements.viewer.resetPages()
		}

		this.preventStickyHeader()
		window.setTimeout(() => {
			if (this.dataset.desktopLayout === 'stacked') {
				activeMedia.scrollIntoView({ behavior: 'smooth' })
			}
		})
		this.playActiveMedia(activeMedia)
	}

	announceLiveRegion(activeItem: HTMLElement, position: string) {
		const image = q.rs<HTMLImageElement>(
			'.product__modal-opener--image img',
			activeItem
		)
		if (!image) return
		image.onload = () => {
			this.elements.liveRegion.setAttribute('aria-hidden', 'false')
			this.elements.liveRegion.innerHTML =
				window.accessibilityStrings.imageAvailable.replace(
					'[index]',
					position
				)
			setTimeout(() => {
				this.elements.liveRegion.setAttribute('aria-hidden', 'true')
			}, 2000)
		}
		image.src = image.src
	}

	playActiveMedia(activeItem: HTMLElement) {
		pauseAllMedia()
		const deferredMedia = q.os<DeferredMedia>('.deferred-media', activeItem)
		if (deferredMedia) deferredMedia.loadContent(false)
	}

	preventStickyHeader() {
		this.stickyHeader =
			this.stickyHeader || q.rs<StickyHeader>('sticky-header')
		if (!this.stickyHeader) return
		this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
	}

	removeListSemantic() {
		if (!isSliderComponent(this.elements.viewer)) return
		this.elements.viewer.slider.setAttribute('role', 'presentation')
		this.elements.viewer.sliderItems?.forEach((slide) =>
			slide.setAttribute('role', 'presentation')
		)
	}

	resetCustomMedia() {
		q.rl('[data-gallery-media-id]', this.elements.viewer).forEach((li) => {
			li.classList.remove('hidden')
		})
	}

	switchCustomMedia(variantId: number) {
		if (!this.elements.viewer || !this.customVariantMedia) {
			this.resetCustomMedia()
			return false
		}
		const customMedia = this.customVariantMedia[`${variantId}`]
		if (!customMedia || customMedia?.length < 1) {
			this.resetCustomMedia()
			return false
		}
		q.rl('[data-gallery-media-id]', this.elements.viewer).forEach((li) => {
			const mediaIdStr = q.ra(li, 'data-gallery-media-id').split('-')
			// get last
			const mediaId = mediaIdStr[mediaIdStr.length - 1]
			if (!customMedia.includes(mediaId.trim())) {
				li.classList.add('hidden')
        li.classList.remove('is-active')
        void window.Ucoast.mediaManager.pauseAllInContainer(li)
			} else {
				li.classList.remove('hidden')
        li.classList.add('is-active')
        void window.Ucoast.mediaManager.playAllInContainer(li)
			}
		})
		window.setTimeout(() => {
			this.elements.viewer.setSlidePosition(0)
		}, 100)
		return true
	}
}
